import { Location } from "vue-router";
import { Route } from "~/enums/route";
import { RouteHelper } from "~/helpers/routeHelper";
import { StringHelper } from "~/helpers/stringHelper";
import { Hash } from "~/interfaces/hashInterfaces";
import IModel from "~/interfaces/modelInterfaces";
import { INavigableValidatableModel } from "~/interfaces/navigableValidationInterfaces";
import { TValidator, TValidatorKey } from "~/interfaces/validationInterfaces";
import { ValidationResultInterface } from "~/interfaces/validationResultInterface";
import ErrorCategories from "~/models/navigableValidations/errorArray";
import NavigableValidator from "~/models/navigableValidators/navigableValidator";
import ValidationError, { IErrorNavOpts, ValidationErrorLevel } from "../validationError";
import ValidationErrorCollection from "./validationErrorCollection";

export class ErrorNavOpts implements IErrorNavOpts {
  beforeReveal: (...args) => void;
  reveal: (targetElementId, containerElementSelector, fallbackContainer) => Promise<JQuery>;
  afterReveal: ($el?: JQuery, ...args) => void;
  containerElementSelector: string;
  elementClientId: string;
  hash: string;
  pageArea: string;
  baseRoute: string;
  path: string;
  query: Hash<string>;
  targetElementId: string;
  useLocation: false | string;

  constructor(options: Partial<IErrorNavOpts>) {
    Object.assign(this, options);
  }

  get locationString(): string {
    let base = "";
    if (this.baseRoute) base += `${this.baseRoute}/`;
    base += `${this.path}`;
    if (Object.values(this.query).length) {
      base += "?";
      Object.keys(this.query).forEach(k => base += `${k}=${this.query[k]}`);
    }
    if (this.hash) {
      base += `#${this.hash}`;
    }
    return base;
  }

  get vueLocation(): Location {
    return {
      path: this.path,
      query: this.query,
      hash: this.hash
    }
  }
}


export default class NavigableValidation<T extends INavigableValidatableModel<T>> {
  model: T;
  readonly baseRoute: Route;
  validators: Array<TValidator<T>>;
  _results: ValidationResultInterface = {};

  /*
   * SECTIONS
   * key is a string which is also the display name for the error modal
   * value is an array of 'field' properties for the validators in the
   * `validators` array
   */

  get SECTIONS(): Hash<Array<TValidatorKey<T>>> {
    const sections = {}
    this.validators.map(v => {
      if (!sections[v.section]) sections[v.section] = [];
      sections[v.section].push(v.validatorKey)
    })
    return sections
  }

  errorCollection: ValidationErrorCollection;

  constructor(model: T) {
    this.init(model);
  }

  init(model: T) {
    this.model = model;
  }

  get builderRoute() {
    return this.baseRoute ? RouteHelper.path({route: this.baseRoute, modelOrId: this.model as (T & IModel)}) : "";
  }

  errorNavOpts(validator: NavigableValidator<T>, validatorKey: string, value?, ...rest): IErrorNavOpts {
    const baseRoute = this.builderRoute || ""
    let path;
    if (validator.options.path) {
      path = validator.options.path;
    } else path = this.getPath(validatorKey, value) || "" ;
    const query = this.buildQuery(validatorKey, value);
    let anchor: string;
    if (validator.options.anchorHash) {
      anchor = validator.options.anchorHash
    } else anchor = validatorKey
    const anchorHash = this.getAnchorHash(anchor, value) || "";
    const beforeReveal = validator.beforeReveal
    const reveal = validator.reveal
    const afterReveal = validator.afterReveal
    const containerElementSelector = validator.containerElementSelector || ""

    return new ErrorNavOpts({
      useLocation: false,
      baseRoute: baseRoute,
      path: path,
      hash: anchorHash,
      query: query,
      elementClientId: "",
      targetElementId: "",
      containerElementSelector: containerElementSelector,
      beforeReveal: beforeReveal,
      reveal: reveal,
      afterReveal: afterReveal,
    })
  };

  protected buildQuery(validatorKey, value?) {
    return {}
  }

  protected getPath(validatorKey, ...rest): string {
    return this.getPageArea(validatorKey) || ""
  }

  protected getAnchorHash(validatorKey: TValidatorKey<T>, ...rest): string {
    return StringHelper.toUnderscore(<string>validatorKey || "").replace(/ /g, '');
  };

  get isValid(): boolean {
    this.validate();
    return this.validators.every(v => v.isValid);
  }

  get errors(): ValidationError[] {
    return this.results.getErrors();
  }

  getSection(validatorKey: TValidatorKey<T>): string {
    let section = "";
    Object.keys(this.SECTIONS).forEach(key => {
      if (this.SECTIONS[key].includes(validatorKey)) {
        section = key;
        return;
      }
    })
    return section;
  }

  getPageArea(validatorKey: TValidatorKey<T>, value?): string {
    const section = this.getSection(validatorKey);
    if (section) {
      return StringHelper.toUnderscore(section);
    } else {
      throw new Error(`Page area not found for prop ${validatorKey}. Add field to SECTIONS array in ${this.constructor.name}. ${Object.keys(this.SECTIONS).join(", ")}`);
    }
  }

  get results(): any {
    let errorCollection = new ValidationErrorCollection(this);
    if (!Object.keys(this._results).length) {
      this.validators.forEach((validator: TValidator<T>) => {
        validator.errors.forEach(error => {
          if (!errorCollection.collection[error.section]) {
            errorCollection.collection[error.section] = ErrorCategories.categories();
          }
          errorCollection.collection[error.section][error.type].push(error)
        })
      });
    }
    this.errorCollection = errorCollection;
    return this.errorCollection
  }

  private validate(level: ValidationErrorLevel = ValidationErrorLevel.All): void {
    this.levelValidators(level).forEach(validator => {
      validator.validate(this.model);
    });
  }

  private levelValidators(level: ValidationErrorLevel = ValidationErrorLevel.All): Array<NavigableValidator<any>> {
    if (level === ValidationErrorLevel.All) {
      return this.validators;
    } else {
      return this.validators.filter(v => v.errorLevel === level);
    }
  }
}